import { yupResolver } from '@hookform/resolvers/yup';
import { Box } from '@material-ui/core';
import { Grid } from '@mui/material';
import { OptionType, SGSFormLayout } from 'components';
import { endOfYear, format, startOfYear } from 'date-fns';
import { useArretTravailFormContext } from 'features/arretTravail/contexts/ArretTravailFormProvider';
import useValidationEffects from 'features/arretTravail/hooks/useValidationEffects';
import { useEffect } from 'react';
import { FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { SalaryBaseS3201 } from '../salaryBaseS3201';
import { useSalaryRegulCotisSchema } from './schema';
import { TableSalaryRegulCotis } from './tableSalaireRegulCotis';
import { TableSalaryInterruptionPeriodS3201 } from './tableSalaryInterruptionPeriodS3201';

type Props = {
    store: any;
    onSubmit: SubmitHandler<SalaryRegulCotisSchema>;
    actions?: JSX.Element;
};

export type SalaryRappelS3201 = {
    id: string;
    startDate: Date | null;
    endDate: Date | null;
    amount: string | null;
};

export type SalaryNotFulTime = {
    id: string;
    period: string;
    amount: string | null;
    reason: string;
    realH: string;
    completH: string;
    restoredSalary: string;
};

export type SalaryRegulCotisSchema = {
    salaryRappelS3201: SalaryRappelS3201[];
    interruptionActivityS3201: SalaryNotFulTime[];
};

export type SalaryBaseSalaires = {
    periods: OptionType[];
};

const FormSalaryInterruptionPeriodS3201: React.FC<Props> = ({ onSubmit, store, actions }) => {
    const { activeStep, setStepFailed } = useArretTravailFormContext();
    const validationSchema: Yup.SchemaOf<SalaryRegulCotisSchema> = useSalaryRegulCotisSchema({ store });

    const accidentDate = store?.workStoppage?.infos?.start ?? null; // TODO :: check if this is the date used to calculate period
    const nbrYear = 1;

    const initialSalaires = Array.from({ length: nbrYear }, (_, index) => {
        const year = new Date(accidentDate).getFullYear();
        return {
            id: `${index + 1}`,
            startDate: format(startOfYear(new Date(year - 1, 0, 1)), 'yyyy/MM/dd'),
            endDate: format(endOfYear(new Date(year - 1, 11, 31)), 'yyyy/MM/dd'),
            amount: null,
        };
    });

    const salaryBaseSalairaires: SalaryBaseS3201[] = store?.salaryBaseS3201 ?? [];
    const periodsOptions = salaryBaseSalairaires.map((salary, index): OptionType => {
        const startDate = new Date(salary.startDate).toLocaleDateString('en-GB');
        const endDate = new Date(salary.endDate).toLocaleDateString('en-GB');
        return {
            value: salary.id,
            label: `Period ${index + 1}: ${startDate} - ${endDate}`,
        };
    });

    const initialSalairyNotFullTime = [
        {
            id: 1,
            period: null,
            amount: null,
            reason: null,
            realH: null,
            completH: null,
            restoredSalary: null,
        },
    ];

    const methods = useForm<SalaryRegulCotisSchema>({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            salaryRappelS3201: store?.salaryRappelS3201?.length ? store.salaryRappelS3201 : initialSalaires.reverse(),
            interruptionActivityS3201: store?.interruptionActivityS3201?.length ? store.interruptionActivityS3201 : initialSalairyNotFullTime.reverse(),
        },
    });

    const {
        handleSubmit,
        control,
        formState: { errors },
    } = methods;

    useValidationEffects({ errors, activeStep, setStepFailed, control });

    const { fields: salaries, append } = useFieldArray<SalaryRegulCotisSchema, 'salaryRappelS3201', 'id'>({
        control,
        name: 'salaryRappelS3201',
        keyName: 'id',
    });

    const {
        fields: salairyNotFillTime,
        append: appendSalairyNotFullTime,
        remove: removeSalairyNotFullTime,
    } = useFieldArray<SalaryRegulCotisSchema, 'interruptionActivityS3201', 'id'>({
        control,
        name: 'interruptionActivityS3201',
        keyName: 'id',
    });

    const handleNewSalaire = () => {
        append({
            id: `${salaries.length + 1}`,
            startDate: null,
            endDate: null,
            amount: null,
        });
    };

    const handleNewSalairyNotFullTime = () => {
        appendSalairyNotFullTime({
            id: `${salaries.length + 1}`,
            period: null,
            amount: null,
            reason: null,
            realH: null,
            completH: null,
            restoredSalary: null,
        });
    };

    useEffect(() => {
        if (salaries.length === 0) handleNewSalaire();
    }, [salaries]);

    useEffect(() => {
        if (salairyNotFillTime.length === 0) handleNewSalairyNotFullTime();
    }, [salairyNotFillTime]);

    console.log(errors);

    return (
        <FormProvider {...methods}>
            <SGSFormLayout onSubmit={handleSubmit(onSubmit)} actions={actions}>
                <Grid container justifyContent="center" style={{ marginBottom: '20px' }}>
                    <Grid item xs={12} md={3}></Grid>
                    <Grid item xs={12} md={6}>
                        <Box className={'mt-4'}>
                            <TableSalaryRegulCotis salaries={salaries} />
                        </Box>
                    </Grid>
                    <Grid item xs={0} md={3}></Grid>
                    <Grid item xs={12}>
                        <Box className={'mt-4'}>
                            <TableSalaryInterruptionPeriodS3201
                                salaries={salairyNotFillTime}
                                appendSalary={handleNewSalairyNotFullTime}
                                removeSalary={removeSalairyNotFullTime}
                                periodsOptions={periodsOptions}
                                store={store}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </SGSFormLayout>
        </FormProvider>
    );
};

export default FormSalaryInterruptionPeriodS3201;
